// ทะเบียนอุปกรณ์ของงานละหมาด — ตอนนี้คือจอที่แขวนหน้ามัสยิด
//
// ทำไมจอต้องมีโทเคนของตัวเอง แทนที่จะให้ใครสักคนล็อกอินค้างไว้:
// token ของคนอยู่ใน sessionStorage ปิดเบราว์เซอร์หรือไฟดับก็หลุด แล้วมัสยิด
// ไม่มีใครอยู่ประจำที่จะไปล็อกอินใหม่ตอนตีห้า — เช้ามาจะไม่มีใครถูกเช็คทั้งรอบ
// และบัญชีครูจริงที่ค้างอยู่บนกล่องไม่มีคนเฝ้า คือบัญชีที่ใครกดออกจากโหมดเต็มจอ
// ก็เอาไปใช้ต่อได้ทั้งใบ ส่วนโทเคนใบนี้ทำได้อย่างเดียวคือรับผลขึ้นจอ
import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Card, Form, Modal, Table } from 'react-bootstrap';
import Swal from 'sweetalert2';
import Header from '../layouts/Header';
import Footer from '../layouts/Footer';
import axiosInstance from '../services/axiosInterceptor';
import { useLoading } from '../contexts/LoadingContext';

const API = process.env.REACT_APP_API;

const thaiDateTime = (v) => (v
  ? new Date(v).toLocaleString('th-TH', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
  : '—');

// จอเขียน last_seen ตอนเปิดหน้าและตอนต่อ socket ใหม่ ไม่ได้เคาะทุกนาที
// จอที่เปิดค้างทั้งวันจึงไม่ขยับ — เกณฑ์ต้องกว้าง ไม่งั้นจะขึ้นออฟไลน์ทั้งที่ปกติ
// (เหตุผลเดียวกับ ONLINE_MS ของจอในหน้าอุปกรณ์ประตู)
const ONLINE_MS = 24 * 60 * 60 * 1000;

const sinceText = (v) => {
  if (!v) return 'ยังไม่เคยเปิด';
  const secs = Math.round((Date.now() - new Date(v).getTime()) / 1000);
  if (secs < 90) return 'เมื่อครู่';
  if (secs < 3600) return `${Math.round(secs / 60)} นาทีที่แล้ว`;
  if (secs < 86400) return `${Math.round(secs / 3600)} ชม.ที่แล้ว`;
  return thaiDateTime(v);
};

export default function AdminPrayerDevices() {
  const setSkin = useState(localStorage.getItem('skin-mode') ? 'dark' : '')[1];
  const { setLoading } = useLoading();

  const [devices, setDevices] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  // แถวที่กำลังแก้อยู่ — null = โหมดเพิ่มใหม่ ใช้ modal ตัวเดียวกันทั้งสองโหมด
  // เพราะช่องที่กรอกเหมือนกัน ต่างแค่รหัสเครื่องที่แก้ไม่ได้ (มันอยู่ใน URL ของจอ
  // ที่เปิดค้างอยู่บนกล่อง เปลี่ยนแล้วจอจะต่อไม่ได้จนกว่าจะมีคนเดินไปแก้ URL)
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ code: '', name: '', lane: '', note: '' });
  const [issued, setIssued] = useState(null);   // { device, token } โผล่ครั้งเดียว
  const [copied, setCopied] = useState(false);
  // ผูกจอด้วยรหัส 6 หลักที่จอขึ้นเอง — ทางปกติของการติดตั้งจอใหม่
  const [showPair, setShowPair] = useState(false);
  const [pairCode, setPairCode] = useState('');
  const [pairInfo, setPairInfo] = useState(null);   // ใบที่เจอจากการพิมพ์เลข
  const [pairDevice, setPairDevice] = useState('');
  const [pairError, setPairError] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get(`${API}/prayer/devices`);
      setDevices(data);
    } catch {
      setDevices([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const displayUrl = (code, token) =>
    `${window.location.origin}/prayer/display?device=${encodeURIComponent(code)}&key=${token}`;

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(displayUrl(issued.device.pdv_code, issued.token));
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      Swal.fire({ icon: 'info', title: 'คัดลอกอัตโนมัติไม่ได้', text: 'ลากคลุมข้อความแล้วคัดลอกเองได้' });
    }
  };

  const openPair = () => {
    setPairCode(''); setPairInfo(null); setPairDevice(''); setPairError('');
    setShowPair(true);
  };

  /**
   * พิมพ์ครบ 6 หลักแล้วตรวจให้เลย ไม่ต้องกดปุ่มเพิ่ม
   *
   * คนที่ทำงานนี้ยืนอยู่หน้าจอที่มัสยิดพร้อมมือถือในมือ ทุกปุ่มที่เพิ่มเข้ามา
   * คือการสลับสายตาระหว่างจอกับมือถืออีกหนึ่งรอบ
   */
  const lookupPair = async (code) => {
    setPairError(''); setPairInfo(null);
    try {
      const { data } = await axiosInstance.post(`${API}/prayer/devices/pairing/lookup`, { pair_code: code });
      setPairInfo(data);
      // รหัสเครื่องที่จอบอกมาเองใช้เป็นตัวเลือกตั้งต้น — ผู้ดูแลยังเปลี่ยนได้
      const hinted = devices.find((d) => d.pdv_code === data.device_hint && d.is_active);
      if (hinted) setPairDevice(String(hinted.pdv_id));
    } catch (error) {
      setPairError(error?.response?.data?.message || 'ตรวจรหัสไม่สำเร็จ');
    }
  };

  const onPairCodeChange = (raw) => {
    const digits = raw.replace(/\D/g, '').slice(0, 6);
    setPairCode(digits);
    if (digits.length === 6) lookupPair(digits);
    else { setPairInfo(null); setPairError(''); }
  };

  const submitPair = async (e) => {
    e.preventDefault();
    const device = devices.find((d) => String(d.pdv_id) === pairDevice);
    if (!device) { setPairError('ต้องเลือกว่าจอนี้คือเครื่องไหน'); return; }

    // เครื่องหนึ่งแถว = กล่องจริงหนึ่งใบ ผูกใหม่แล้วจอเดิมที่ถือโทเคนใบเก่าจะดับ
    // ต้องบอกก่อน ไม่ใช่ให้ไปรู้ตอนเดินไปดูแล้วเจอจอดำ
    if (device.last_seen_at) {
      const ok = await Swal.fire({
        icon: 'warning',
        title: `${device.pdv_name} เคยมีจอผูกอยู่แล้ว`,
        html: `เปิดล่าสุด ${sinceText(device.last_seen_at)}<br/>ผูกจอใหม่แล้วจอเดิมของเครื่องนี้จะดับทันที`,
        showCancelButton: true, confirmButtonText: 'ผูกจอใหม่', cancelButtonText: 'ยกเลิก',
      });
      if (!ok.isConfirmed) return;
    }

    setLoading(true);
    try {
      await axiosInstance.post(`${API}/prayer/devices/pairing/approve`, {
        pair_code: pairCode, device_id: device.pdv_id,
      });
      setShowPair(false);
      load();
      Swal.fire({
        icon: 'success',
        title: 'ผูกจอแล้ว',
        text: `จอที่ขึ้นเลข ${pairCode} จะเริ่มแสดงผลของ ${device.pdv_name} ภายในไม่กี่วินาที`,
      });
    } catch (error) {
      setPairError(error?.response?.data?.message || 'ผูกจอไม่สำเร็จ');
    } finally {
      setLoading(false);
    }
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axiosInstance.post(`${API}/prayer/devices`, form);
      setShowAdd(false);
      setForm({ code: '', name: '', lane: '', note: '' });
      setIssued(data);
      load();
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'เพิ่มอุปกรณ์ไม่สำเร็จ', text: error?.response?.data?.message || '' });
    } finally {
      setLoading(false);
    }
  };

  const rotate = async (device) => {
    const ok = await Swal.fire({
      icon: 'warning',
      title: `ออกโทเคนใหม่ให้ ${device.pdv_name}?`,
      text: 'โทเคนใบเดิมจะใช้ไม่ได้ทันที จอเครื่องนั้นจะดับจนกว่าจะเปิดด้วย URL ใหม่',
      showCancelButton: true, confirmButtonText: 'ออกโทเคนใหม่', cancelButtonText: 'ยกเลิก',
    });
    if (!ok.isConfirmed) return;
    try {
      const { data } = await axiosInstance.post(`${API}/prayer/devices/${device.pdv_id}/rotate`);
      setIssued(data);
      load();
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'ออกโทเคนใหม่ไม่สำเร็จ', text: error?.response?.data?.message || '' });
    }
  };

  const openEdit = (device) => {
    setEditing(device);
    setForm({
      code: device.pdv_code, name: device.pdv_name,
      lane: device.pdv_lane || '', note: device.pdv_note || '',
    });
    setShowAdd(true);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.put(`${API}/prayer/devices/${editing.pdv_id}`, {
        name: form.name, lane: form.lane, note: form.note,
        is_active: !!editing.is_active,
      });
      setShowAdd(false);
      setEditing(null);
      setForm({ code: '', name: '', lane: '', note: '' });
      load();
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'แก้ข้อมูลไม่สำเร็จ', text: error?.response?.data?.message || '' });
    }
  };

  const toggleActive = async (device) => {
    try {
      await axiosInstance.put(`${API}/prayer/devices/${device.pdv_id}`, {
        name: device.pdv_name, lane: device.pdv_lane,
        note: device.pdv_note, is_active: !device.is_active,
      });
      load();
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'เปลี่ยนสถานะไม่สำเร็จ', text: error?.response?.data?.message || '' });
    }
  };

  return (
    <>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div>
            <h4 className="main-title mb-0">อุปกรณ์งานละหมาด</h4>
            <div className="fs-xs text-secondary">
              จอที่แขวนหน้ามัสยิด — แต่ละเครื่องมีโทเคนของตัวเอง ไม่ต้องให้ใครล็อกอินค้างไว้
            </div>
          </div>
          <div className="d-flex gap-2">
            {/* ปุ่มนี้มาก่อน "เพิ่มจอ" เพราะเป็นงานที่ทำบ่อยกว่า: แถวในทะเบียน
                สร้างครั้งเดียว ส่วนการผูกจอเกิดใหม่ทุกครั้งที่เปลี่ยนกล่อง/ล้างเครื่อง */}
            <Button variant="primary" onClick={openPair}>
              <i className="ri-links-line me-1"></i>ผูกจอด้วยรหัส
            </Button>
            <Button variant="outline-primary" onClick={() => {
              // ล้างฟอร์มทุกครั้งที่กดเพิ่ม — ไม่งั้นจะได้ค่าของจอที่เพิ่งแก้ค้างอยู่
              setEditing(null);
              setForm({ code: '', name: '', lane: '', note: '' });
              setShowAdd(true);
            }}>
              <i className="ri-add-line me-1"></i>เพิ่มเครื่องในทะเบียน
            </Button>
          </div>
        </div>

        <Card className="card-one">
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table className="mb-0 align-middle">
                <thead>
                  <tr>
                    <th>รหัสเครื่อง</th>
                    <th>ชื่อ</th>
                    <th>เปิดล่าสุด</th>
                    <th>สถานะ</th>
                    <th style={{ width: 200 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {devices.length === 0 ? (
                    <tr><td colSpan={5} className="text-center text-secondary py-4">
                      ยังไม่มีเครื่องในทะเบียน — กด “เพิ่มเครื่องในทะเบียน” ตั้งชื่อกับช่องทางไว้ก่อน
                      แล้วค่อยเปิด <code>/prayer/display</code> ที่ทีวีเพื่อเอาเลข 6 หลักมาผูก
                    </td></tr>
                  ) : devices.map((d) => {
                    const online = d.last_seen_at && (Date.now() - new Date(d.last_seen_at).getTime()) < ONLINE_MS;
                    return (
                      <tr key={d.pdv_id} className={d.is_active ? '' : 'opacity-50'}>
                        <td><code>{d.pdv_code}</code></td>
                        <td>
                          <div>{d.pdv_name}</div>
                          {/* ช่องทางต้องเห็นในตาราง ไม่ใช่ซ่อนในฟอร์มแก้ไข — เวลาชื่อ
                              นักเรียนไม่ขึ้นจอ คำถามแรกคือ "จอนี้ผูกช่องไหนอยู่" */}
                          {d.pdv_lane
                            ? <Badge bg="info" className="mt-1">ช่อง {d.pdv_lane}</Badge>
                            : <Badge bg="light" text="dark" className="mt-1">จอรวม (เห็นทุกช่อง)</Badge>}
                          {d.pdv_note && <div className="fs-xs text-secondary">{d.pdv_note}</div>}
                        </td>
                        <td>
                          <div className="fs-sm">{sinceText(d.last_seen_at)}</div>
                          {d.last_seen_at && <div className="fs-xs text-secondary">{thaiDateTime(d.last_seen_at)}</div>}
                        </td>
                        <td>
                          {!d.is_active ? <Badge bg="secondary">ปิดใช้งาน</Badge>
                            : online ? <Badge bg="success">เปิดอยู่</Badge>
                              : <Badge bg="warning">ไม่ได้เปิดมานาน</Badge>}
                        </td>
                        <td className="text-end">
                          <Button size="sm" variant="outline-primary" className="me-1" onClick={() => openEdit(d)}>
                            แก้ไข
                          </Button>
                          <Button size="sm" variant="outline-primary" className="me-1" onClick={() => rotate(d)}>
                            ออกโทเคนใหม่
                          </Button>
                          <Button size="sm" variant={d.is_active ? 'outline-secondary' : 'outline-success'}
                            onClick={() => toggleActive(d)}>
                            {d.is_active ? 'ปิดใช้งาน' : 'เปิดใช้งาน'}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>

        <div className="fs-xs text-secondary mt-3">
          ติดตั้งจอใหม่: เปิด <code>/prayer/display</code> ที่กล่องต่อทีวี (ไม่ต้องมีอะไรต่อท้าย)
          จอจะขึ้นเลข 6 หลัก เอาเลขนั้นมากด “ผูกจอด้วยรหัส” ที่นี่ —
          ไม่มีโทเคนเดินทางผ่าน URL จึงไม่ค้างอยู่ใน log ของเซิร์ฟเวอร์หรือในแชตที่ส่งให้ช่าง
          <br />
          จอหายหรือถูกยกไป ให้กด “ปิดใช้งาน” ของใบนั้น ไม่ต้องแตะใบอื่น —
          และไม่มีทางอ่านโทเคนเดิมซ้ำโดยตั้งใจ ลืมแล้วให้ผูกใหม่หรือออกใบใหม่
        </div>

        <Footer />
      </div>

      {/* เพิ่มจอ */}
      <Modal show={showAdd} onHide={() => { setShowAdd(false); setEditing(null); }} centered>
        <Form onSubmit={editing ? submitEdit : submitAdd}>
          <Modal.Header closeButton>
            <Modal.Title className="fs-16">
              {editing ? `แก้ไข ${editing.pdv_code}` : 'เพิ่มจอ'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>รหัสเครื่อง</Form.Label>
              <Form.Control
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                placeholder="masjid-1"
                required
                disabled={!!editing}
              />
              <Form.Text>
                {editing
                  ? 'รหัสเครื่องแก้ไม่ได้ เพราะมันอยู่ใน URL ที่เปิดค้างอยู่บนกล่องที่ต่อทีวี'
                  : 'ใช้ได้เฉพาะ a-z 0-9 - _ — รหัสนี้จะอยู่ใน URL ของจอ'}
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>ชื่อ</Form.Label>
              <Form.Control
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="จอหน้ามัสยิด (ทางออกฝั่งขวา)"
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>ช่องทางเดินออก</Form.Label>
              <Form.Control
                value={form.lane}
                onChange={(e) => setForm({ ...form, lane: e.target.value })}
                placeholder="right"
              />
              <Form.Text>
                กล้องกับจอที่อยู่จุดเดียวกันต้องใส่ค่านี้ให้ตรงกัน จอจะขึ้นเฉพาะชื่อคนที่
                เดินผ่านช่องนี้ — <strong>เว้นว่างไว้ = จอรวม</strong> เห็นทุกช่อง
                (เหมาะกับจอในห้องครู ไม่เหมาะกับจอที่นักเรียนต้องหาชื่อตัวเอง)
              </Form.Text>
            </Form.Group>
            <Form.Group>
              <Form.Label>หมายเหตุ</Form.Label>
              <Form.Control
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder="ทีวี 43 นิ้ว + กล่องมินิพีซี"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => { setShowAdd(false); setEditing(null); }}>ยกเลิก</Button>
            <Button type="submit" variant="primary">
              {editing ? 'บันทึก' : 'เพิ่มและออกโทเคน'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* ผูกจอด้วยรหัสที่จอขึ้นเอง — ทางปกติของการติดตั้ง ไม่มีโทเคนผ่าน URL */}
      <Modal show={showPair} onHide={() => setShowPair(false)} centered>
        <Form onSubmit={submitPair}>
          <Modal.Header closeButton><Modal.Title className="fs-16">ผูกจอด้วยรหัส</Modal.Title></Modal.Header>
          <Modal.Body>
            <div className="fs-sm text-secondary mb-3">
              เปิด <code>/prayer/display</code> ที่กล่องซึ่งต่อกับทีวี จอจะขึ้นเลข 6 หลัก
              เอาเลขนั้นมากรอกที่นี่ แล้วชี้ว่าจอตัวนั้นคือเครื่องไหนในทะเบียน
            </div>

            <Form.Group className="mb-3">
              <Form.Label>เลขที่ขึ้นบนจอ</Form.Label>
              <Form.Control
                value={pairCode}
                onChange={(e) => onPairCodeChange(e.target.value)}
                inputMode="numeric"
                autoFocus
                placeholder="000000"
                className="text-center"
                style={{ fontSize: '2rem', letterSpacing: '.4rem', fontVariantNumeric: 'tabular-nums' }}
              />
              <Form.Text>พิมพ์ครบ 6 หลักแล้วระบบจะตรวจให้เอง</Form.Text>
            </Form.Group>

            {pairError && <div className="alert alert-danger fs-sm py-2">{pairError}</div>}

            {pairInfo && (
              <>
                <div className="alert alert-success fs-sm py-2">
                  เจอจอที่กำลังรออยู่ — ขอรหัสเมื่อ {sinceText(pairInfo.created_at)}
                  {pairInfo.device_hint && <> · จอบอกว่าตัวเองคือ <code>{pairInfo.device_hint}</code></>}
                </div>
                <Form.Group>
                  <Form.Label>จอนี้คือเครื่องไหน</Form.Label>
                  <Form.Select value={pairDevice} onChange={(e) => setPairDevice(e.target.value)} required>
                    <option value="">— เลือกเครื่อง —</option>
                    {devices.filter((d) => d.is_active).map((d) => (
                      <option key={d.pdv_id} value={d.pdv_id}>
                        {d.pdv_code} — {d.pdv_name}{d.pdv_lane ? ` (ช่อง ${d.pdv_lane})` : ' (จอรวม)'}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text>
                    เลือกผิดแล้วจอจะขึ้นชื่อคนของอีกช่องหนึ่ง — ผูกใหม่ได้ ไม่ต้องลบอะไร
                  </Form.Text>
                </Form.Group>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowPair(false)}>ปิด</Button>
            <Button type="submit" variant="primary" disabled={!pairInfo || !pairDevice}>ผูกจอ</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* โทเคนโผล่ครั้งเดียว — ปิดหน้าต่างนี้แล้วอ่านซ้ำไม่ได้ */}
      <Modal show={!!issued} onHide={() => setIssued(null)} centered size="lg">
        <Modal.Header closeButton><Modal.Title className="fs-16">URL สำหรับเปิดที่จอ</Modal.Title></Modal.Header>
        <Modal.Body>
          {issued && (
            <>
              <div className="alert alert-warning fs-sm">
                <b>คัดลอกตอนนี้เลย</b> — ปิดหน้าต่างนี้แล้วจะอ่านโทเคนซ้ำไม่ได้อีก ลืมแล้วต้องออกใบใหม่
              </div>
              <div className="mb-2 fs-sm text-secondary">เอา URL นี้ไปเปิดบนกล่องที่ต่อกับทีวี แล้ว bookmark ไว้</div>
              <div className="p-3 rounded bg-body-tertiary" style={{ wordBreak: 'break-all', fontFamily: 'monospace' }}>
                {displayUrl(issued.device.pdv_code, issued.token)}
              </div>
              <Button className="mt-3" variant={copied ? 'success' : 'primary'} onClick={copyUrl}>
                <i className={`${copied ? 'ri-check-line' : 'ri-file-copy-line'} me-1`}></i>
                {copied ? 'คัดลอกแล้ว' : 'คัดลอก URL'}
              </Button>
              <div className="fs-xs text-secondary mt-3">
                พอเปิดครั้งแรก หน้าจอจะเก็บโทเคนไว้ในเครื่องแล้วถอดออกจากแถบที่อยู่ทันที
                เพื่อไม่ให้โทเคนค้างอยู่บนจอที่แขวนในที่สาธารณะ — บูตครั้งต่อไปเปิดแค่
                <code className="mx-1">/prayer/display?device={issued.device.pdv_code}</code>ก็พอ
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setIssued(null)}>คัดลอกแล้ว ปิดหน้าต่าง</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
